import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, getCurrentInstance, watch, nextTick } from "vue";
import CURadioButton from "@/components/CURadioButton.vue";
import CUTextField from "@/components/CUTextField.vue";
import PaymentProfileSelectorV2 from "@/components/PaymentProfileSelectorV2.vue";
import CreditCardFormV2 from "@/components/CreditCardFormV2.vue";
import AlertBox from "@/components/AlertBox.vue";
import payment from "@/services/payment";
import {
  PaymentMethodTypeKey,
  PaymentGatewayTypeKey,
  CompanyId,
} from "@/utils/enum";
import confetti from "@/store/modules/confetti";
import { AxiosError } from "axios";
import { StatusCodes } from "http-status-codes";
import { useGoogleAnalytics, Event } from "@/composables/useGoogleAnalytics";



export default /*#__PURE__*/_defineComponent({
  __name: 'MakeCreditCardPayment',
  props: {
    reservation: null,
    balancePaymentMethods: null,
    isOpen: { type: Boolean },
    isBookingContact: { type: Boolean }
  },
  emits: ["payment-success", "close"],
  setup(__props: any, { emit }) {

const props = __props as {
  reservation: any;
  balancePaymentMethods: any[];
  isOpen?: boolean;
  isBookingContact?: boolean;
};

const { proxy } = getCurrentInstance()!;
const t = proxy.$t.bind(proxy);






const isMakingPartialPayment = ref(false);
const otherAmount = ref<number | null>(null);
const otherAmountInput = ref("");
const selectedPaymentProfile = ref(null);
const hasPaymentProfiles = ref(null);
const loadingPaymentProfiles = ref(false);
const showCardForm = ref(false);
const newCard = ref(null);
const loading = ref(false);
const errorMessage = ref<string | null>(null);
const submitDisabled = ref(false);
const paymentProfileKey = ref(0);

const textFieldRef = ref();
const creditCardFormRef = ref();

const analytics = useGoogleAnalytics()

const remainingBalance = computed(() => {
  return formatCurrency(props.reservation.balance);
});

const formatCurrency = (value: number): string => {
  if (isNaN(value)) return "";
  return Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const onCurrencyInput = async (event: any) => {
  // Get the input value
  const inputValue = String(event?.target?.value ?? event);

  // Remove all non-numeric characters except decimal point and comma
  const cleanedValue = inputValue.replace(/[^\d.,]/g, "");

  // If letters were detected, immediately update with cleaned value
  if (cleanedValue !== inputValue) {
    otherAmountInput.value = cleanedValue;
    await nextTick();
    return;
  }

  // Get only digits from the input
  const raw = inputValue.replace(/\D/g, "");
  // Parse as cents
  const cents = parseInt(raw, 10) || 0;
  // Convert to dollars
  const value = cents / 100;
  otherAmount.value = value;
  // Format as currency string
  otherAmountInput.value = value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const onShowCardForm = () => {
  showCardForm.value = true;
  submitDisabled.value = true;
};

const close = () => {
  resetForm();
  emit("close");
};

const resetForm = () => {
  isMakingPartialPayment.value = false;
  otherAmount.value = null;
  otherAmountInput.value = "";
  selectedPaymentProfile.value = null;
  showCardForm.value = false;
  newCard.value = null;
  errorMessage.value = null;
  submitDisabled.value = false;
  paymentProfileKey.value++;

  // Reset CC form
  if (creditCardFormRef.value && creditCardFormRef.value.reset) {
    creditCardFormRef.value.reset();
  }
};

const handleSubmissionError = (error: AxiosError): void => {
  loading.value = false;

  const { status, data } = error.response || {};
  console.error(
    `Error processing payment for reservation: ${props.reservation.reservationId}`,
    data?.message
  );

  const is409Error = status === StatusCodes.CONFLICT;
  const is5xxError = status >= 500 && status < 600;
  const shouldObfuscateError = is409Error || is5xxError;

  const obfuscatedError =
    "There was an issue processing your payment. Please try again.";
  const message = data?.message || t("checkout.PAYMENT_ERROR").toString();
  errorMessage.value = shouldObfuscateError ? obfuscatedError : message;
};

const submit = async () => {
  let success = false;
  const amount = isMakingPartialPayment.value
    ? otherAmount.value
    : props.reservation.balance;

  try {
    if (selectedPaymentProfile.value && !showCardForm.value) {
      success = await collectPaymentFromExistingMethod(amount);
    } else if (showCardForm.value) {
      const paymentForm = creditCardFormRef.value;
      const cardForm = paymentForm["card-form"];
      const cardFormValid =
        cardForm && cardForm.validate ? cardForm.validate() : true;
      const addressForm = paymentForm?.$refs["address-form"];
      const addressFormValid =
        addressForm && addressForm.validate ? addressForm.validate() : true;
      if (addressFormValid && cardFormValid) {
        success = await collectPaymentFromNewMethod(amount);
      }
    } else if (!selectedPaymentProfile.value && !showCardForm.value) {
      errorMessage.value = "Please select a payment method.";
    }
    if (success) {
      trackPaymentSubmission()
      emit("payment-success");
      otherAmountInput.value = "";
      otherAmount.value = null;
      isMakingPartialPayment.value = false;
      newCard.value = null;
      showCardForm.value = false;
      errorMessage.value = null;

      if (confetti && confetti.setShow) confetti.setShow();
    }
  } catch (err: any) {
    handleSubmissionError(err);
  }
};

async function collectPaymentFromExistingMethod(amount: number) {
  let isSuccessful = false;
  loading.value = true;
  try {
    const paymentGatewayResponse = await payment.getDefaultGateway(
      CompanyId.CharterUP
    );
    const paymentGatewayData = paymentGatewayResponse.data.paymentGateways[0];
    const paymentGateway = {
      id: paymentGatewayData.companyPaymentGatewayId,
      key: paymentGatewayData.paymentGatewayTypeKey,
    };
    const payload = {
      payment_method: PaymentMethodTypeKey.CreditCard,
      payment_gateway: paymentGateway,
      paymentProfileId: selectedPaymentProfile.value?.customerPaymentProfileId,
      reservationIds: [props.reservation.reservationId],
      reservationToVersionMap: {
        [props.reservation.reservationId]: props.reservation.reservationVersion,
      },
      sendEmail: true,
      amount: amount || 0,
    };
    await payment.collect(payload);
    isSuccessful = true;
  } catch (err: any) {
    handleSubmissionError(err);
  }
  loading.value = false;
  return isSuccessful;
}

async function collectPaymentFromNewMethod(amount: number) {
  let isSuccessful = false;
  loading.value = true;
  let tokenizedCardInfo;
  try {
    const finixPaymentGateway = await getFinixPaymentGateway();
    if (!finixPaymentGateway || !finixPaymentGateway.id) {
      loading.value = false;
      return isSuccessful;
    }
    const finixForm = creditCardFormRef.value as any;
    const token = await finixForm.getTokenizedFinixInfo();
    tokenizedCardInfo = {
      tokens: [token],
      paymentGateway: finixPaymentGateway,
    };
  } catch (error) {
    console.error(error);
  }
  const billing = {
    ...newCard.value,
    cardholderName: newCard.value?.name,
  };

  const formattedPaymentInfo = {
    billing,
    payment_gateway: null,
    nonces: null,
    payment_method: null,
    reservationIds: null,
    reservationToVersionMap: {},
    sendEmail: null,
    amount: null,
    notes: null,
    paymentProfileId: null,
    showOnCharterUp: null,
  };
  if (tokenizedCardInfo?.paymentGateway && tokenizedCardInfo?.tokens) {
    formattedPaymentInfo.payment_gateway = tokenizedCardInfo.paymentGateway;
    formattedPaymentInfo.nonces = tokenizedCardInfo.tokens;
    formattedPaymentInfo.payment_method = PaymentMethodTypeKey.CreditCard;
    formattedPaymentInfo.reservationIds = [props.reservation.reservationId];
    formattedPaymentInfo.reservationToVersionMap = {
      [props.reservation.reservationId]: props.reservation.reservationVersion,
    };
    formattedPaymentInfo.sendEmail = true;
    formattedPaymentInfo.amount = amount;
    formattedPaymentInfo.showOnCharterUp = newCard.value?.showOnCharterUp;
    try {
      await payment.collect(formattedPaymentInfo);
      isSuccessful = true;
    } catch (err: any) {
      handleSubmissionError(err);
    }
  } else {
    errorMessage.value = t("paymentInformation.CARD_ERROR_MESSAGE").toString();
  }
  loading.value = false;
  return isSuccessful;
}

const trackPaymentSubmission = (): void => {
  const amount = isMakingPartialPayment.value
    ? otherAmount.value
    : props.reservation.balance;

  analytics.trackEvent(Event.PartialPayment, {
    amount: amount,
    value: amount, // GA4 built-in parameter for monetary values (can be summed)
    currency: 'USD',
    payment_type: isMakingPartialPayment.value ? "partial" : "full",
    is_booking_contact: props.isBookingContact,

  })
}

async function getFinixPaymentGateway() {
  const defaultPaymentGatewayResponse = await payment.getPaymentGateways();
  const finixGateway = defaultPaymentGatewayResponse.data.paymentGateways
    .filter(
      (paymentGateway: any) => paymentGateway.companyId === CompanyId.CharterUP
    )
    .filter((paymentGateway: any) => paymentGateway.checkoutPageId === null)
    .filter(
      (paymentGateway: any) =>
        paymentGateway.paymentGatewayTypeKey === PaymentGatewayTypeKey.Finix
    )?.[0];
  return {
    id: finixGateway?.companyPaymentGatewayId,
    key: finixGateway?.paymentGatewayTypeKey,
  };
}

const onBack = () => {
  showCardForm.value = false;
  submitDisabled.value = false;
};

const currencyInputRules = [
  (v: string) => {
    const value = parseFloat(v.replace(/[^0-9.]/g, ""));
    if (isNaN(value) || value <= 0) return t("paymentInformation.AMOUNT_MUST_BE_GREATER_THAN_0");
    if (value > props.reservation.balance)
      return t("paymentInformation.AMOUNT_CANNOT_EXCEED_REMAINING_BALANCE");
    return true;
  },
];

const onFormErrors = (hasErrors: boolean) => {
  submitDisabled.value = hasErrors;
};

// Auto-show card form when no payment profiles exist
watch(() => hasPaymentProfiles.value, (newVal) => {
  if (newVal === false) {
    showCardForm.value = true;
  }
});

// Reset form when dialog closes
watch(() => props.isOpen, (newVal, oldVal) => {
  if (oldVal === true && newVal === false) {
    resetForm();
  } else if (oldVal === false && newVal === true) {
    // When dialog opens, increment key to force PaymentProfileSelectorV2 to re-mount
    paymentProfileKey.value++;
    // When dialog opens, check if we should show card form
    if (hasPaymentProfiles.value === false) {
      showCardForm.value = true;
    }
  }
});





const formattedErrorMessage = computed(() => {
  return errorMessage.value?.replace(/;.*/, "");
});

const detailText = computed(() => {
  if (props.isBookingContact) {
    return t("paymentInformation.BOOKING_CONTACT_CC_DETAIL");
  }
  return t("paymentInformation.AUTH_USER_CC_DETAIL");
});

return { __sfc: true,proxy, t, props, emit, isMakingPartialPayment, otherAmount, otherAmountInput, selectedPaymentProfile, hasPaymentProfiles, loadingPaymentProfiles, showCardForm, newCard, loading, errorMessage, submitDisabled, paymentProfileKey, textFieldRef, creditCardFormRef, analytics, remainingBalance, formatCurrency, onCurrencyInput, onShowCardForm, close, resetForm, handleSubmissionError, submit, collectPaymentFromExistingMethod, collectPaymentFromNewMethod, trackPaymentSubmission, getFinixPaymentGateway, onBack, currencyInputRules, onFormErrors, formattedErrorMessage, detailText, CURadioButton, CUTextField, PaymentProfileSelectorV2, CreditCardFormV2, AlertBox }
}

})